import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TableContainer,
  Card,
  CardContent,
  Chip,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  Stack,
  Avatar,
  Fab,
} from "@mui/material";
import {
  Visibility,
  Assignment,
  Refresh,
  Close,
  CheckCircle,
  Pending,
  ArrowBack,
} from "@mui/icons-material";
import axios from "axios";
import { baseURL, config, requestID } from "../../../common/utils";
import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { useHistory } from "react-router";

const UserRecommendation: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const router = useIonRouter();


  const goBack = () => {
    history

  }

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError("");
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseURL}/Onboardings/getRecommendationByUserId`,
        payload,
        config
      );
      setRecommendations(Array.isArray(res.data.data) ? res.data.data : [res.data.data]);
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError("Failed to load user recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const handleViewMore = (message: string) => {
    setSelectedMessage(message);
    setOpenModal(true);
  };

  const handleBack2 = () => {

      history.push("/folder/my-recommendations/View%20Recommendations");

};

  
  const getStatusChip = (status: string) => {
    const statusConfig = {
      "0": { 
        label: "Pending", 
        color: "warning", 
        icon: <Pending />,
      },
      "1": { 
        label: "Approved", 
        color: "success", 
        icon: <CheckCircle />,
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      label: "Unknown",
      color: "default",
    };

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color as any}
        variant="outlined"
        size="small"
      />
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <IonPage className="pageDown">
        <IonContent className="ion-padding">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="80vh"
            flexDirection="column"
            gap={3}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading recommendations...
            </Typography>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="pageDown">
      <IonContent className="ion-padding">
        <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
          {/* Back Button */}
          <Fab
            onClick={handleBack2}
            sx={{
              position: "fixed",
              top: 20,
              left: 20,
              zIndex: 1000,
              bgcolor: "background.paper",
              color: "text.primary",
              boxShadow: 2,
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
            size="medium"
          >
            <ArrowBack />
          </Fab>

          {/* Header */}
          <AppBar
            position="static"
            elevation={0}
            sx={{ bgcolor: "background.paper", color: "text.primary", mb: 4 }}
          >
            <Toolbar>
              <Assignment sx={{ mr: 2, color: "primary.main" }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" component="h1" fontWeight="600">
                  My Recommendations
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage and view your recommendation requests
                </Typography>
              </Box>
              <IconButton
                onClick={fetchRecommendations}
                title="Refresh recommendations"
              >
                <Refresh />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Container maxWidth="lg" sx={{ pb: 6 }}>
            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
                action={
                  <Button color="inherit" size="small" onClick={fetchRecommendations}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Typography variant="h3" fontWeight="700" color="primary.main" gutterBottom>
                      {recommendations.length}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Total Requests
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Typography variant="h3" fontWeight="700" color="success.main" gutterBottom>
                      {recommendations?.filter(r => r && r.status === "1").length}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Approved
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Typography variant="h3" fontWeight="700" color="warning.main" gutterBottom>
                      {recommendations?.filter(r => r && r.status === "0")?.length}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Pending
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Recommendations Table */}
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ p: 3, bgcolor: "primary.main", color: "white" }}>
                <Typography variant="h5" fontWeight="600">
                  Recommendation History
                </Typography>
              </Box>

              <TableContainer>
                <Table size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Message Preview</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recommendations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <Box sx={{ textAlign: "center" }}>
                            <Avatar sx={{ bgcolor: "grey.100", width: 80, height: 80, mx: "auto", mb: 2 }}>
                              <Assignment sx={{ fontSize: 40, color: "grey.400" }} />
                            </Avatar>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              No recommendations found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              Your recommendation requests will appear here once submitted.
                            </Typography>
                            <Button variant="contained" startIcon={<Assignment />}>
                              Request Recommendation
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recommendations?.map((rec: any, index: number) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": { bgcolor: "action.hover" },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {formatDate(rec?.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 400 }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                              {rec?.message?.length > 100 ? (
                                <>
                                  {rec.message.substring(0, 100)}...
                                  <Box
                                    component="span"
                                    onClick={() => handleViewMore(rec?.message)}
                                    sx={{
                                      color: "primary.main",
                                      cursor: "pointer",
                                      ml: 0.5,
                                      fontWeight: 600,
                                      fontSize: "0.875rem",
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                  >
                                    View More
                                  </Box>
                                </>
                              ) : (
                                rec?.message || "—"
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(rec?.status)}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleViewMore(rec?.message)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Quick Actions */}
            {recommendations.length > 0 && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchRecommendations}
                >
                  Refresh
                </Button>


                <Button
                  variant="contained"
                  onClick={handleBack2}
                  startIcon={<Assignment />}

                >
                  New Recommendation
                </Button>
              </Stack>
            )}
          </Container>
        </Box>

        {/* View More Modal */}
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box display="flex" alignItems="center">
              <Assignment sx={{ mr: 1 }} />
              Recommendation Details
            </Box>
            <IconButton onClick={() => setOpenModal(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <DialogContentText
              sx={{ 
                whiteSpace: "pre-line", 
                fontSize: "1rem", 
                lineHeight: 1.6,
                p: 2,
                borderRadius: 1,
                bgcolor: "grey.50"
              }}
            >
              {selectedMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenModal(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default UserRecommendation;
